/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type ClinicUser,
  type ClinicUserInvite,
  type InviteClinicUserInput,
  clinicUsersApi,
} from '../api/clinic-users.js';

const USERS_KEY = ['clinic-users'] as const;

export function useClinicUsers(): UseQueryResult<readonly ClinicUser[]> {
  return useQuery({ queryKey: USERS_KEY, queryFn: () => clinicUsersApi.list() });
}

export function useInviteClinicUser(): UseMutationResult<
  ClinicUserInvite,
  Error,
  InviteClinicUserInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteClinicUserInput) => clinicUsersApi.invite(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

interface SetStatusVars {
  readonly id: string;
  readonly status: 'active' | 'disabled';
}

export function useSetUserStatus(): UseMutationResult<readonly ClinicUser[], Error, SetStatusVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: SetStatusVars) => clinicUsersApi.setStatus(vars.id, vars.status),
    onSuccess: (rows) => {
      qc.setQueryData(USERS_KEY, rows);
    },
  });
}
